import { useEffect, useState, useRef, createContext, useContext, useCallback } from "react";
import "@/App.css";
import "@/brand-theme.css";
import "@/navigation.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useNavigate, Link, useParams } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { ImageEditorProvider } from "./components/image-editor/ImageEditorProvider";

// Pages
import SplashPage from "./pages/SplashPage";
import LandingPage from "./pages/LandingPage";
import Dashboard from "./pages/Dashboard";
import ArtistProfile from "./pages/ArtistProfile";
import Fandom from "./pages/Fandom";
import EventPage from "./pages/EventPage";
import ProductPage from "./pages/ProductPage";
import PaymentSuccess from "./pages/PaymentSuccess";
import CheckoutComplete from "./pages/CheckoutComplete";
import MyTickets from "./pages/MyTickets";
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import CopyrightPolicy from "./pages/CopyrightPolicy";
import SecurityTrust from "./pages/SecurityTrust";
import SessionsAndSecurity from "./pages/SessionsAndSecurity";
import AdminLeakDetector from "./pages/AdminLeakDetector";
import NotificationBell from "./components/NotificationBell";
import OwnerPassportGate from "./components/OwnerPassportGate";
import PrivacyReport from "./pages/PrivacyReport";
import PublicStatus from "./pages/PublicStatus";
import Top100Idols from "./pages/Top100Idols";
import Awards2027 from "./pages/Awards2027";
import AccountRecovery from "./pages/AccountRecovery";
import SellerOrders from "./pages/SellerOrders";
import MyOrders from "./pages/MyOrders";
import TicketScanner from "./pages/TicketScanner";
import FeedPage from "./pages/FeedPage";
import AdminLogin from "./pages/AdminLogin";
import MessagesPage from "./pages/MessagesPage";
import AuthPage from "./pages/AuthPage";
import Partnerships from "./pages/Partnerships";
import ResetPassword from "./pages/ResetPassword";
import CorporateDashboard from "./pages/CorporateDashboard";
import CorporateStorePublic from "./pages/CorporateStorePublic";
import MyExperiences from "./pages/MyExperiences";
import AIMessages from "./pages/AIMessages";
import TodaysFeed from "./pages/TodaysFeed";  // Now Headlines
import Community from "./pages/Community";
import MyCollectibles from "./pages/MyCollectibles";
import FanDashboard from "./pages/FanDashboard";
import Wallet from "./pages/Wallet";
import AdminPayouts from "./pages/AdminPayouts";
import AdminExternalPayouts from "./pages/AdminExternalPayouts";
import TeachingCenter from "./pages/TeachingCenter";
import AdminRefunds from "./pages/AdminRefunds";
import AdminIdolApplications from "./pages/AdminIdolApplications";
import AdminSponsors from "./pages/AdminSponsors";
import AdminAwaitingReview from "./pages/AdminAwaitingReview";
import AdminBuyers from "./pages/AdminBuyers";
import AdminArtistsDirectory from "./pages/AdminArtistsDirectory";
import AdminTaxBuckets from "./pages/AdminTaxBuckets";
import IDVault from "./pages/IDVault";
import AdminVerifications from "./pages/AdminVerifications";
import CRADashboard from "./pages/CRADashboard";
import Expenses from "./pages/Expenses";
import MessageAdmin from "./pages/MessageAdmin";
import PublicWall from "./pages/PublicWall";
import SponsorContribute from "./pages/SponsorContribute";
import SupportChat from "./components/SupportChat";
import CartDrawer from "./components/CartDrawer";
import CartFloatingButton from "./components/CartFloatingButton";
import RecoveryReminderBanner from "./components/RecoveryReminderBanner";
import LiveSaleTicker from "./components/LiveSaleTicker";
import { CartProvider } from "./context/CartContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [artistProfile, setArtistProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem("session_token"));

  const checkAuth = useCallback(async () => {
    const savedToken = localStorage.getItem("session_token");
    if (!savedToken) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${savedToken}` }
      });
      setUser({
        ...response.data.user,
        is_admin: response.data.is_admin,
        kyc_status: response.data.kyc_status || response.data.user?.kyc_status || "not_started",
      });
      setArtistProfile(response.data.artist_profile);
      // Auto-rotated session token (24h+ old session got refreshed silently)
      if (response.data.rotated_session?.session_token) {
        const newTok = response.data.rotated_session.session_token;
        localStorage.setItem("session_token", newTok);
        setToken(newTok);
      } else {
        setToken(savedToken);
      }
    } catch (error) {
      console.error("Auth check failed:", error);
      localStorage.removeItem("session_token");
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = (sessionToken, userData, artistData = null) => {
    localStorage.setItem("session_token", sessionToken);
    setToken(sessionToken);
    setUser({
      ...userData,
      is_admin: userData?.is_admin || false,
      kyc_status: userData?.kyc_status || "not_started",
    });
    setArtistProfile(artistData);
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (e) {
      console.error("Logout error:", e);
    }
    localStorage.removeItem("session_token");
    setToken(null);
    setUser(null);
    setArtistProfile(null);
  };

  const refreshArtistProfile = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setArtistProfile(response.data.artist_profile);
    } catch (e) {
      console.error("Refresh artist profile failed:", e);
    }
  };

  const refreshUser = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser({ ...response.data.user, is_admin: response.data.is_admin });
      setArtistProfile(response.data.artist_profile);
    } catch (e) {
      console.error("Refresh user failed:", e);
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      artistProfile,
      token,
      loading,
      login,
      logout,
      refreshArtistProfile,
      refreshUser,
      isAuthenticated: !!user,
      isArtist: !!artistProfile
    }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, requireArtist = false, allowUnverified = false }) => {
  const { isAuthenticated, isArtist, loading, user } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050810]">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  if (requireArtist && !isArtist) {
    return <Navigate to="/dashboard" replace />;
  }

  // The Sentry — every protected route pivots unverified users into the
  // ID Vault until an admin (or the AI vision scan) marks them approved.
  // `/id-vault` itself passes `allowUnverified` so users can complete KYC.
  const kyc = user?.kyc_status || "not_started";
  if (!allowUnverified && !user?.is_admin && kyc !== "approved") {
    if (location.pathname !== "/id-vault") {
      return <Navigate to="/id-vault" replace state={{ from: location, kyc_status: kyc }} />;
    }
  }

  return children;
};

// Global 423 (Locked) interceptor — if any API returns
// {code: "kyc_required"} the sentry pivots the tab to /id-vault
// regardless of which page tried to make the call.
axios.interceptors.response.use(
  (r) => r,
  (err) => {
    try {
      const status = err?.response?.status;
      const code = err?.response?.data?.detail?.code;
      const redirect = err?.response?.data?.detail?.redirect;
      if (status === 423 && code === "kyc_required" && typeof window !== "undefined") {
        const target = redirect || "/id-vault";
        if (window.location.pathname !== target) {
          window.location.assign(target);
        }
      }
    } catch { /* ignore */ }
    return Promise.reject(err);
  },
);

// Corporate users have their own dashboard — redirect them away from the artist/fan dashboard
const DashboardRouter = () => {
  const { user } = useAuth();
  if (user?.role === "corporate") {
    return <Navigate to="/corporate-dashboard" replace />;
  }
  return <Dashboard />;
};

// Auth Page Wrapper to pass login from context
const AuthPageWrapper = () => {
  const { login } = useAuth();
  const handleLogin = async (sessionToken, seed = null) => {
    // Fast path — /auth/login now returns { user, is_admin } directly so
    // we can flip the admin UI on the SAME render without waiting on a
    // /auth/me round-trip. We still fetch /auth/me in the background to
    // populate artist_profile / corporate_profile for the header.
    if (seed?.user) {
      login(sessionToken, { ...seed.user, is_admin: !!seed.is_admin }, null);
      // fire-and-forget profile enrichment
      axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${sessionToken}` } })
        .then((r) => login(sessionToken, { ...r.data.user, is_admin: !!r.data.is_admin }, r.data.artist_profile))
        .catch(() => {});
      return { ...seed.user, is_admin: !!seed.is_admin };
    }
    try {
      const res = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${sessionToken}` } });
      login(sessionToken, { ...res.data.user, is_admin: res.data.is_admin }, res.data.artist_profile);
      return { ...res.data.user, is_admin: res.data.is_admin };
    } catch {
      login(sessionToken, {});
      return null;
    }
  };
  return <AuthPage onLogin={handleLogin} />;
};

// App Router — email + password auth only (Google/Emergent OAuth removed).
function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<SplashPage />} />
      <Route path="/home" element={<LandingPage />} />
      <Route path="/dashboard/*" element={
        <ProtectedRoute>
          <DashboardRouter />
        </ProtectedRoute>
      } />
      <Route path="/corporate-dashboard" element={
        <ProtectedRoute>
          <CorporateDashboard />
        </ProtectedRoute>
      } />
      <Route path="/corporate/:corporateId" element={<CorporateStorePublic />} />
      <Route path="/fandom" element={<Fandom />} />
      <Route path="/feed" element={
        <ProtectedRoute>
          <FeedPage />
        </ProtectedRoute>
      } />
      <Route path="/artist/:artistId" element={<ArtistProfile />} />
      <Route path="/events/:eventId" element={<EventPage />} />
      <Route path="/products/:productId" element={<ProductPage />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/checkout/complete" element={
        <ProtectedRoute>
          <CheckoutComplete />
        </ProtectedRoute>
      } />
      <Route path="/my-tickets" element={
        <ProtectedRoute>
          <MyTickets />
        </ProtectedRoute>
      } />
      <Route path="/my-experiences" element={
        <ProtectedRoute>
          <MyExperiences />
        </ProtectedRoute>
      } />
      <Route path="/my-ai-messages" element={
        <ProtectedRoute>
          <AIMessages />
        </ProtectedRoute>
      } />
      <Route path="/my-collectibles" element={
        <ProtectedRoute>
          <MyCollectibles />
        </ProtectedRoute>
      } />
      <Route path="/fan-dashboard" element={
        <ProtectedRoute>
          <FanDashboard />
        </ProtectedRoute>
      } />
      <Route path="/wallet" element={
        <ProtectedRoute>
          <Wallet />
        </ProtectedRoute>
      } />
      <Route path="/security/sessions" element={
        <ProtectedRoute>
          <SessionsAndSecurity />
        </ProtectedRoute>
      } />
      <Route path="/admin/payouts" element={
        <ProtectedRoute>
          <AdminPayouts />
        </ProtectedRoute>
      } />
      <Route path="/admin/payouts/external" element={
        <ProtectedRoute>
          <AdminExternalPayouts />
        </ProtectedRoute>
      } />
      <Route path="/teaching-center" element={<TeachingCenter />} />
      <Route path="/learn" element={<TeachingCenter />} />
      <Route path="/admin/refunds" element={
        <ProtectedRoute>
          <AdminRefunds />
        </ProtectedRoute>
      } />
      <Route path="/admin/idol-applications" element={
        <ProtectedRoute>
          <AdminIdolApplications />
        </ProtectedRoute>
      } />
      <Route path="/admin/sponsors" element={
        <ProtectedRoute>
          <AdminSponsors />
        </ProtectedRoute>
      } />
      <Route path="/admin/awaiting-review" element={
        <ProtectedRoute>
          <AdminAwaitingReview />
        </ProtectedRoute>
      } />
      <Route path="/admin/buyers" element={
        <ProtectedRoute>
          <AdminBuyers />
        </ProtectedRoute>
      } />
      <Route path="/admin/artists" element={
        <ProtectedRoute>
          <AdminArtistsDirectory />
        </ProtectedRoute>
      } />
      <Route path="/admin/tax-buckets" element={
        <ProtectedRoute>
          <AdminTaxBuckets />
        </ProtectedRoute>
      } />
      <Route path="/admin/verifications" element={
        <ProtectedRoute>
          <AdminVerifications />
        </ProtectedRoute>
      } />
      <Route path="/admin/cra" element={
        <ProtectedRoute>
          <CRADashboard />
        </ProtectedRoute>
      } />
      <Route path="/id-vault" element={
        <ProtectedRoute allowUnverified>
          <IDVault />
        </ProtectedRoute>
      } />
      <Route path="/expenses" element={
        <ProtectedRoute>
          <Expenses />
        </ProtectedRoute>
      } />
      <Route path="/admin" element={<AdminLogin />} />
      <Route path="/top-100" element={<Top100Idols />} />
      <Route path="/awards/2027" element={<Awards2027 />} />
      <Route path="/awards" element={<Awards2027 />} />
      <Route path="/auth" element={<AuthPageWrapper />} />
      <Route path="/auth/reset-password" element={<ResetPassword />} />
      <Route path="/partnerships" element={<Partnerships />} />
      <Route path="/todays-feed" element={<TodaysFeed />} />
      <Route path="/headlines" element={<TodaysFeed />} />
      <Route path="/community" element={<Community />} />
      <Route path="/community/:artistId" element={<Community />} />
      <Route path="/messages" element={
        <ProtectedRoute>
          <MessagesPage />
        </ProtectedRoute>
      } />
      <Route path="/messages/:artistId" element={
        <ProtectedRoute>
          <MessagesPage />
        </ProtectedRoute>
      } />
      <Route path="/message-admin" element={
        <ProtectedRoute>
          <MessageAdmin />
        </ProtectedRoute>
      } />
      <Route path="/u/:identifier" element={<PublicWall />} />
      <Route path="/sponsors" element={<SponsorContribute />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/copyright" element={<CopyrightPolicy />} />
      <Route path="/security" element={<SecurityTrust />} />
      <Route path="/privacy-report" element={<PrivacyReport />} />
      <Route path="/status" element={<PublicStatus />} />
      <Route path="/top-100-global-idols-2027" element={<Top100Idols />} />
      <Route path="/auth/recover" element={<AccountRecovery />} />
      <Route path="/seller/orders" element={
        <ProtectedRoute><SellerOrders /></ProtectedRoute>
      } />
      <Route path="/my-orders" element={
        <ProtectedRoute><MyOrders /></ProtectedRoute>
      } />
      <Route path="/my-purchases" element={
        <ProtectedRoute><MyOrders /></ProtectedRoute>
      } />
      <Route path="/scan" element={
        <ProtectedRoute><TicketScanner /></ProtectedRoute>
      } />
      <Route path="/admin/leak-detector" element={
        <ProtectedRoute><AdminLeakDetector /></ProtectedRoute>
      } />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <ImageEditorProvider>
          <CartProvider>
            <RecoveryReminderBanner />
            <AppRouter />
            <LiveSaleTicker />
            <CartFloatingButton />
            <CartDrawer />
            <SupportChat />
            {/* Owner-only passport gate — non-blocking for everyone else. */}
            <OwnerPassportGate />
            {/* Global top-right notification bell — poll + blink for admin
                inbox threats, Day-11 wire queue, and personal alerts. */}
            <div className="fixed top-4 right-4 z-50" data-testid="notification-bell-wrapper">
              <NotificationBell />
            </div>
            <Toaster 
              position="top-right"
              toastOptions={{
                style: {
                  background: 'var(--surface)',
                  border: '1px solid #1A2030',
                  color: '#F8F9FA'
                }
              }}
            />
          </CartProvider>
          </ImageEditorProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
